// Segment adaptive-icon engine config + flag (ENGINE side only).
// --------------------------------------------------------------------------
// This module owns the FUNCTIONAL mechanism for the "adaptive segment icon"
// feature on the wheel renderer (createPie.js). It does NOT own the icon
// visual library, the icon set, or any renderer-v2 visual rebuild — that is
// Fable's surface. Everything here is size-adaptive logic + color plumbing.
//
// Pairs with nothing on the backend — this is pure client render logic.
// createPie.js unconditionally calls updateIcons() in updateArcs() to render a
// per-segment icon that adapts to the segment's available size (full icon ->
// dot -> nothing).

// Deck `adaptive-icons` white-default contrast rule (WL-012). Pure; delegates
// the WCAG math to utils/contrast. Wired into resolveSegmentIconColor below so
// the wheel renderer paints per-wedge-readable icons instead of a fixed ink.
import { adaptiveIconColor } from "../SegmentBuilder/Shared/SegmentStyling/logic/adaptiveIconColor";

// --------------------------------------------------------------------------
// Adaptive-display thresholds (tunable constants, not magic numbers).
//
// The renderer measures each segment's ANGULAR span in radians
// (arc.endAngle - arc.startAngle). A full circle is 2*PI (~6.283 rad). These
// thresholds answer "is there enough room to draw something here?":
//
//   arcAngle >= MIN_ANGLE_FOR_FULL_ICON  -> draw the full icon
//   MIN_ANGLE_FOR_DOT <= arcAngle < FULL  -> draw a dot (icon too cramped)
//   arcAngle <  MIN_ANGLE_FOR_DOT        -> draw nothing (no room even for a dot)
//
// Defaults preserve the dev's original numbers (0.25 / 0.10 rad) so behavior
// is identical to his intended pass; they live here so they can be tuned in
// one place or overridden per-call via config.segmentIconConfig.
//
// NOTE (documented limitation): angular span alone ignores radius. A segment
// is also bounded by the ring thickness (pieOutterRadius - pieInnerRadius);
// at a fixed angle a thicker ring has more pixel room. v1 keys off angle only
// (matching the dev's pass). RADIAL_PIXEL_FLOOR below is an optional, OFF-by-
// default secondary guard the renderer can apply when it knows its radii, so a
// future tune can incorporate true pixel size without changing this contract.
//
// SIZE FLOOR (MIN_WHEEL_DIAMETER_FOR_ICONS): angle is NOT size. A 4-segment
// wheel has wide per-segment angles at ANY diameter, so an angle-only gate lets
// a 46px profile/thumbnail mini-wheel draw full 20px wedge icons — the glitchy
// aesthetic Gordon reported. The deck's UNIVERSAL RESPONSIVE RATIO LAW
// (design-handoff concepts/adaptive-icons-elevated §07, Gordon verbatim) is a
// rendered-DIAMETER law: the size ladder is Full 360 -> Large 240 -> Small 150
// (icons shown, proportional to the Portrait), then a dashed SMALL THRESHOLD,
// then Avatar 64 -> Icon 32 -> Favicon 16 where "the Portrait locks to a certain
// size and the Wedges begin to shrink and the icons disappear." So below a
// minimum rendered wheel diameter, NO wedge icons draw on ANY segment,
// regardless of angle. Small=150 is the smallest icon-bearing size in the deck,
// so 150px is the floor (>=150 => icons allowed; <150 => none). This gate is
// applied only when the renderer supplies a real diameter, so pure/unit callers
// (unknown geometry) are unchanged.
export const SEGMENT_ICON_THRESHOLDS = {
  // radians of arc span
  MIN_ANGLE_FOR_FULL_ICON: 0.25,
  MIN_ANGLE_FOR_DOT: 0.1,
  // optional pixel floor on the chord at the centroid; <=0 disables it.
  RADIAL_PIXEL_FLOOR: 0,
  // minimum RENDERED wheel diameter (px) below which NO wedge icons draw on any
  // segment, regardless of angle. <=0 disables it. Deck SMALL THRESHOLD = 150.
  MIN_WHEEL_DIAMETER_FOR_ICONS: 150,
};

// --------------------------------------------------------------------------
// showOnSegment resolution (wheel-level "what draws on each segment").
//
// createPie.js's segmentIconConfig.showOnSegment gates updateIcons() (draws when
// "icons"/"both") and updateNumberLable() (draws when "numbers"/"both"). Its
// hardcoded default is "numbers", which — because updateArcs() calls only
// updateIcons() — renders NOTHING on a segment. The wheel-owner's persisted
// choice lives in the redux `segment_icon` field (default "blank") and is saved
// through the existing wheel payload (generatePayload -> `show_segment_icon`);
// this resolver maps that persisted value into the engine's showOnSegment,
// gated by SEGMENT_ADAPTIVE_ICONS_ENABLED so the flag-OFF surface is unchanged.

// The engine default. Kept as a named constant so callers and tests can assert
// "byte-identical when unset / flag OFF" against one source of truth.
export const SHOW_ON_SEGMENT_DEFAULT = "numbers";

// The only persisted values that actually paint per-segment icons via
// updateIcons(). "numbers"/"blank" both render nothing today (updateNumberLable
// is not called), so they collapse to the default.
export const SHOW_ON_SEGMENT_ICON_VALUES = ["icons", "both"];

// Resolve the wheel-level showOnSegment the renderer should use.
//   segmentIcon  — the wheel's persisted `segment_icon` setting (may be any of
//                  "blank" | "numbers" | "icons" | "both", or unset).
//   flagEnabled  — SEGMENT_ADAPTIVE_ICONS_ENABLED (pass it in; keeps this pure).
// Flag OFF (or unset segmentIcon / a non-icon value) -> SHOW_ON_SEGMENT_DEFAULT,
// which is byte-identical to today (the engine's own default; nothing draws).
// Only an explicit "icons"/"both" under an ON flag activates the icon render
// path. Case/whitespace-tolerant so a stray stored value can't break the SVG.
export const resolveShowOnSegment = (segmentIcon, flagEnabled = false) => {
  if (!flagEnabled) return SHOW_ON_SEGMENT_DEFAULT;
  const v =
    typeof segmentIcon === "string" ? segmentIcon.trim().toLowerCase() : "";
  return SHOW_ON_SEGMENT_ICON_VALUES.includes(v) ? v : SHOW_ON_SEGMENT_DEFAULT;
};

// Build the flag-gated `segmentIconConfig` object a <WheelComponent> consumer
// spreads into its `config` prop. This is the SINGLE source of truth for the
// "...(FLAG ? { segmentIconConfig: {...} } : {})" wiring, so EVERY published
// render surface of a wheel (ViewWheel desktop/landscape AND its MobileView
// portrait delegate) wires the exact same way and cannot drift apart — the
// recurring bug where a mobile-portrait early-return renders the same public
// wheel via a separate config the fix forgot to touch.
//   showSegmentIcon — the wheel's persisted BE round-trip value under
//                     `show_segment_icon` (blank/both/none). NOT the internal
//                     builder-redux `segment_icon` key (which never rides the
//                     API response).
//   flagEnabled     — SEGMENT_ADAPTIVE_ICONS_ENABLED (passed in; keeps pure).
// Flag OFF -> returns {} so spreading it is BYTE-IDENTICAL to the pre-feature
// config (the engine keeps its "numbers" default; nothing draws). Flag ON ->
// { segmentIconConfig: { showOnSegment: resolveShowOnSegment(...) } }.
export const segmentIconConfigSpread = (
  showSegmentIcon,
  flagEnabled = false,
  segments = null
) => {
  if (!flagEnabled) return {};
  const effective = resolveEffectiveShowSegmentIcon(
    showSegmentIcon,
    segments,
    flagEnabled
  );
  return {
    segmentIconConfig: {
      showOnSegment: resolveShowOnSegment(effective, flagEnabled),
    },
  };
};

// --------------------------------------------------------------------------
// FE-canonical persisted values for the wheel-level segment-icon setting.
//
// The BE stores this as `show_segment_icon` with a DECLARED choice set of
// blank | both | none (yuga_wheel SegmentIconType; migration 0101). There is
// NO "icons" and NO "numbers" choice server-side. Of the three BE choices,
// only "both" paints per-segment icons in the current engine: updateArcs()
// calls updateIcons() (gated on "icons"/"both") but NEVER updateNumberLable(),
// so "both" renders ICONS-ONLY today (which matches the deck's icons-only
// intent). So the builder persists exactly two values:
//   NONE  = "blank"  -> nothing draws (BE-legal; also what the BE seeds for
//                       AI-generated wheels — premium_ai_wheels)
//   ICONS = "both"   -> adaptive per-segment icons (the only BE choice the
//                       renderer paints)
// We deliberately do NOT persist "icons": it is engine-legal but NOT in the BE
// choice set (the BE only tolerates it because it does not full_clean() the
// choices at write time). "both" is the closest BE-legal value that renders.
export const SEGMENT_ICON_SETTING = {
  NONE: "blank",
  ICONS: "both",
};

// Normalize any stored / hydrated `show_segment_icon` value (blank/both/none,
// legacy "icons", stray case/whitespace, or a non-string/undefined) into the
// FE-canonical setting, so the builder control, the live preview, and the save
// payload all stay inside the BE choice domain. Only "both" (or legacy "icons")
// means icons; everything else collapses to NONE ("blank").
export const normalizeSegmentIconSetting = (raw) => {
  const v = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return v === SEGMENT_ICON_SETTING.ICONS || v === "icons"
    ? SEGMENT_ICON_SETTING.ICONS
    : SEGMENT_ICON_SETTING.NONE;
};

// True when any segment carries a persisted icon identifier (the picker stores
// displayName strings on settings.icon). Used to infer the wheel-level display
// mode when per-segment icons exist but show_segment_icon was left at "blank".
export const wheelHasPersistedSegmentIcons = (segments) => {
  if (!Array.isArray(segments)) return false;
  return segments.some((segment) => {
    const settings = segment && segment.settings;
    const name =
      (settings && settings.icon) || (segment && segment.icon) || "";
    return typeof name === "string" && name.trim() !== "";
  });
};

// T22.90: does THIS segment's chart datum carry its own icon to draw? Only a
// resolved icon element (settings.icon -> iconElementFromName, already null
// when the segment's "Show icon on segment" toggle is off) counts. A segment
// without one draws nothing -- there is no placeholder glyph any more.
export const hasOwnSegmentIcon = (datum) =>
  Boolean(datum && datum.icon && typeof datum.icon === "object");

// Resolve the effective wheel-level display choice for rendering. When the flag
// is ON and any segment has a persisted icon, treat the wheel as icon-display
// even if show_segment_icon is still "blank" (the common UX trap).
export const resolveEffectiveShowSegmentIcon = (
  showSegmentIcon,
  segments,
  flagEnabled = false
) => {
  if (!flagEnabled) return showSegmentIcon;
  const v =
    typeof showSegmentIcon === "string" ? showSegmentIcon.trim().toLowerCase() : "";
  if (SHOW_ON_SEGMENT_ICON_VALUES.includes(v)) return v;
  if (wheelHasPersistedSegmentIcons(segments)) return SEGMENT_ICON_SETTING.ICONS;
  return normalizeSegmentIconSetting(showSegmentIcon);
};

// Icon pixel sizing (full vs. dot).
export const SEGMENT_ICON_SIZES = {
  iconSize: 20,
  dotIconSize: 8,
  numberLableSize: 12,
};

// --------------------------------------------------------------------------
// Color resolution.
//
// Priority:
//   1. per-segment user override  (segment.iconColor — additive data field)
//   2. theme-responsive default   (light theme -> dark icon, dark -> light)
//   3. auto-contrast vs. the segment fill (the dev's getColorContrast)
//
// (3) is passed in by the renderer (it already computes contrast). (2) reads
// the current theme. We keep the resolver pure + injectable so createPie.js
// (a non-React class) can call it without importing React hooks.

// Theme-responsive default icon color. Reads the OS/app color scheme. Light
// theme wants a dark icon; dark theme wants a light icon. These two hexes are
// the single source of truth for the responsive default.
export const ADAPTIVE_ICON_DEFAULT_COLORS = {
  onLightTheme: "#1A1A2E", // near-black/navy, readable on a light page
  onDarkTheme: "#F5F5F7", // near-white, readable on a dark page
};

// Detect dark mode without a React hook (createPie is a plain class). Safe in
// SSR/no-matchMedia environments (returns false -> light default).
export const prefersDarkTheme = () => {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  } catch (e) {
    return false;
  }
};

// Validate a user-supplied color override. Only accept hex (#rgb / #rrggbb) so
// a malformed/empty value falls through to the responsive default instead of
// breaking the SVG fill. Returns the trimmed hex or null.
export const normalizeIconColor = (value) => {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v) ? v : null;
};

// Resolve the final icon color for one segment.
//   segment            — the chart-data datum (may carry .iconColor)
//   autoContrastColor  — color the renderer already computed (getColorContrast)
//   opts.preferAutoContrast — when true, use auto-contrast as the default
//                              instead of the theme-responsive default. Lets a
//                              caller keep the dev's exact old behavior.
export const resolveSegmentIconColor = (
  segment,
  autoContrastColor,
  opts = {}
) => {
  const override = normalizeIconColor(segment && segment.iconColor);
  if (override) return override;

  if (opts.preferAutoContrast && autoContrastColor) {
    return autoContrastColor;
  }

  // Deck `adaptive-icons` white-default rule (opt-in via opts.adaptiveContrast).
  // Pick per-WEDGE: default WHITE, on-brand navy (#123051) ONLY where white
  // washes out on a light wedge fill (contrast(white, fill) < 2.0). This is the
  // deck's documented intent + the builder tooltip's "readable contrast color",
  // and is theme-INDEPENDENT because the icon sits ON the wedge, not on the page
  // — so a fixed theme ink (#1A1A2E) going invisible on a dark navy wedge
  // (measured 1.27:1) is exactly the defect this fixes. Only trusted for an
  // evaluable (solid hex) fill; a non-solid / unparseable fill falls through to
  // the theme default below so the icon still respects the page theme.
  if (opts.adaptiveContrast) {
    const picked = adaptiveIconColor(segment && segment.color);
    if (picked && picked.reason !== "non-solid-fill-default-white") {
      return picked.color;
    }
  }

  const themeDefault = prefersDarkTheme()
    ? ADAPTIVE_ICON_DEFAULT_COLORS.onDarkTheme
    : ADAPTIVE_ICON_DEFAULT_COLORS.onLightTheme;

  // Fall back to auto-contrast only if theme default is somehow unavailable.
  return themeDefault || autoContrastColor || "";
};

// Classify a segment's available size into one of: "full" | "dot" | "none".
// Pure + unit-testable. arcAngle is in radians. ringThicknessPx is optional;
// when RADIAL_PIXEL_FLOOR > 0 and ringThicknessPx is provided, a segment that
// passes the angle gate but is too THIN radially is demoted one rung.
// wheelDiameterPx is optional; when provided and below
// MIN_WHEEL_DIAMETER_FOR_ICONS, the whole wheel is too small for wedge icons
// and EVERY segment returns "none" regardless of angle (deck SMALL THRESHOLD).
export const classifyIconDisplay = (
  arcAngle,
  thresholds = SEGMENT_ICON_THRESHOLDS,
  ringThicknessPx = null,
  wheelDiameterPx = null
) => {
  const {
    MIN_ANGLE_FOR_FULL_ICON,
    MIN_ANGLE_FOR_DOT,
    RADIAL_PIXEL_FLOOR,
    MIN_WHEEL_DIAMETER_FOR_ICONS,
  } = thresholds;

  // Hard size floor: below the minimum rendered wheel diameter, draw NO wedge
  // icons at all — angle alone can't stop a small-but-few-segment thumbnail
  // (e.g. a 46px profile mini-wheel) from rendering full icons. Only enforced
  // when the renderer supplies a real diameter; callers that omit it (unit
  // tests, unknown geometry) keep the original angle-only behavior.
  if (
    typeof wheelDiameterPx === "number" &&
    wheelDiameterPx > 0 &&
    typeof MIN_WHEEL_DIAMETER_FOR_ICONS === "number" &&
    MIN_WHEEL_DIAMETER_FOR_ICONS > 0 &&
    wheelDiameterPx < MIN_WHEEL_DIAMETER_FOR_ICONS
  ) {
    return "none";
  }

  if (arcAngle < MIN_ANGLE_FOR_DOT) return "none";

  let level = arcAngle >= MIN_ANGLE_FOR_FULL_ICON ? "full" : "dot";

  // Optional radial guard: a very thin ring can't fit a full icon even with a
  // wide angle. Demote full -> dot when the ring is below the pixel floor.
  if (
    level === "full" &&
    RADIAL_PIXEL_FLOOR > 0 &&
    typeof ringThicknessPx === "number" &&
    ringThicknessPx < RADIAL_PIXEL_FLOOR
  ) {
    level = "dot";
  }

  return level;
};
